import { useCallback, useMemo } from "react";

import { useRouter } from "next/router";

import { ParsedUrlQuery } from "querystring";

import { objectKeys } from "@/common/utils/object.utils";
import { useAdsURLFiltering } from "@/components/Account/ads/hooks/useAdsURLFiltering";

/**
 * Multi-select status filter for the account lists (decision A42), on the
 * same URL contract My Ads uses: repeated `?status=` values, page reset to 1
 * on every change, so the state survives a reload. `statusIds` must be a
 * module-level constant so the URL parser is not rebuilt every render.
 */
export const useAccountStatusFilter = <T extends string>(
  statusIds: readonly T[]
) => {
  const { query, push } = useRouter();
  const { filtering, onToggleFilter } = useAdsURLFiltering<T>(
    "status",
    statusIds as T[]
  );

  const activeStatuses = useMemo(() => {
    if (!filtering) {
      return [] as T[];
    }

    return objectKeys(filtering).filter(
      status => filtering[status]?.active
    ) as T[];
  }, [filtering]);

  const clearFilters = useCallback(() => {
    const nextQuery: ParsedUrlQuery = { ...query, page: "1" };
    delete nextQuery.status;

    push({ query: nextQuery }, undefined, { shallow: true });
  }, [push, query]);

  return {
    activeStatuses,
    isFiltered: activeStatuses.length > 0,
    activeFilterCount: activeStatuses.length,
    toggleFilter: onToggleFilter,
    clearFilters,
  };
};

/** A row passes when nothing is selected or its status is one of the selected. */
export const matchesAccountStatusFilter = <T extends string>(
  status: T,
  activeStatuses: readonly T[]
): boolean => activeStatuses.length === 0 || activeStatuses.includes(status);
